// Libraries
import _ from 'lodash';
import React from 'react';

// Supermove
import {Space, Styled} from '@supermove/components';
import {useResponsive} from '@supermove/hooks';
import {UserModel} from '@supermove/models';
import {colors} from '@supermove/styles';
import {Phone} from '@supermove/utils';

// App
import ErrorCallout from '@shared/design/components/Callout/ErrorCallout';
import ActionPanel from '@shared/design/components/Panel/ActionPanel';
import EditPanel from '@shared/design/components/Panel/EditPanel';
import Text from '@shared/design/components/Text';
import InviteMoverStep from '@shared/modules/User/enums/InviteMoverStep';
import JobUserMoverPosition from 'modules/JobUser/components/JobUserMoverPosition';

const Row = Styled.View`
  flex-direction: row;
`;

const Container = Styled.View`
  height: 100%;
`;

const Panel = ({
  title,
  BodyComponent,
  handleEdit,
}: {
  title: string;
  BodyComponent: React.ComponentType<any>;
  handleEdit: () => void;
}) => {
  return (
    <ActionPanel
      title={title}
      ActionButtonComponent={() => (
        <EditPanel.EditButton handleEdit={handleEdit} isEditing={false} isDisabled={false} />
      )}
      BodyComponent={BodyComponent}
    />
  );
};

const InfoField = ({label, value}: {label: string; value: string}) => {
  const responsive = useResponsive();
  return (
    <React.Fragment>
      <ActionPanel.LabelText responsive={responsive}>{label}</ActionPanel.LabelText>
      <Space height={8} />
      {value ? (
        <ActionPanel.Text responsive={responsive}>{value}</ActionPanel.Text>
      ) : (
        <Text.Body style={{fontStyle: 'italic', color: colors.gray.tertiary}}>None</Text.Body>
      )}
    </React.Fragment>
  );
};

const MoveDetailsInfoField = ({label, form, field}: {label: string; form: any; field: string}) => {
  const responsive = useResponsive();

  const features = _.get(form.values, `${field}.features`);
  const viewCustomerInfo = features.VIEW_CUSTOMER_INFO;
  return (
    <React.Fragment>
      <ActionPanel.LabelText responsive={responsive}>{label}</ActionPanel.LabelText>
      <Space height={8} />
      <ActionPanel.Text responsive={responsive}>{viewCustomerInfo ? 'Yes' : 'No'}</ActionPanel.Text>
    </React.Fragment>
  );
};

const PositionsInfoField = ({
  label,
  form,
  field,
  viewer,
}: {
  label: string;
  form: any;
  field: string;
  viewer: UserModel;
}) => {
  const responsive = useResponsive();

  return (
    <React.Fragment>
      <ActionPanel.LabelText responsive={responsive}>{label}</ActionPanel.LabelText>
      <Space height={8} />
      <Row style={{flex: 1, flexWrap: 'wrap'}}>
        {_.get(form.values, `${field}.moverPositionIds`).map(
          (moverPositionId: number, index: number) => {
            const moverPosition = viewer.viewingOrganization.companySettings.allMoverPositions.find(
              (moverPosition: any) => _.toString(moverPosition.id) === _.toString(moverPositionId),
            );
            const isPrimary =
              _.toString(moverPositionId) ===
              _.toString(_.get(form.values, `${field}.primaryMoverPositionId`));
            return (
              <React.Fragment key={moverPositionId}>
                {index > 0 && <Space width={4} />}
                <JobUserMoverPosition.Badge
                  isPrimary={isPrimary}
                  color={colors.gray.secondary}
                  name={moverPosition?.name}
                  isDisabled
                  style={{marginVertical: 2}}
                />
              </React.Fragment>
            );
          },
        )}
      </Row>
    </React.Fragment>
  );
};

const UserInfoPanel = ({
  form,
  field,
  setCurrentStepKind,
  viewer,
}: {
  form: any;
  field: string;
  setCurrentStepKind: (kind: string) => void;
  viewer: UserModel;
}) => {
  const kind = InviteMoverStep.USER_INFO;
  const step = InviteMoverStep.getStep(kind);
  const formErrors = _.flattenDeep(_.values(_.get(form.errors, `${field}`)));

  const organization = viewer.viewingOrganization;

  return (
    <Panel
      title={step.title}
      handleEdit={() => setCurrentStepKind(kind)}
      BodyComponent={() => {
        return (
          <React.Fragment>
            {!!formErrors.length &&
              formErrors.map((error: string, index: number) => (
                <React.Fragment key={index}>
                  <ErrorCallout text={error} />
                  <Space height={16} />
                </React.Fragment>
              ))}
            <InfoField label='First Name' value={_.get(form.values, `${field}.names.0`)} />
            <Space height={16} />
            <InfoField label='Last Name' value={_.get(form.values, `${field}.names.1`)} />
            <Space height={16} />
            <InfoField
              label='Phone Number'
              value={Phone.display(_.get(form.values, `${field}.phoneNumber`))}
            />
            <Space height={16} />
            <PositionsInfoField label='Position' form={form} field={field} viewer={viewer} />
            <Space height={16} />
            <MoveDetailsInfoField label='Can View Move Details' form={form} field={field} />
            <Space height={16} />
            <InfoField label='Tablet Passcode' value={_.get(form.values, `${field}.tabletPin`)} />
            <Space height={16} />
            <InfoField
              label='Payroll Identifier'
              value={_.get(form.values, `${field}.payrollIdentifier`)}
            />
            {organization.features.isEnabledUserBillingHourlyRate && (
              <React.Fragment>
                <Space height={16} />
                <InfoField
                  label='Compensation Hourly Rate'
                  value={_.get(form.values, `${field}.billingHourlyRate`)}
                />
              </React.Fragment>
            )}
            {organization.features.isEnabledUserCompensationPercentage && (
              <React.Fragment>
                <Space height={16} />
                <InfoField
                  label='Compensation %'
                  value={_.get(form.values, `${field}.compensationPercentage`)}
                />
              </React.Fragment>
            )}
            {organization.shouldCreateCommercialMoves && (
              <React.Fragment>
                <Space height={16} />
                <InfoField label='Branch Code' value={_.get(form.values, `${field}.branchCode`)} />
              </React.Fragment>
            )}
          </React.Fragment>
        );
      }}
    />
  );
};

// TODO(Hammad) replace viewer prop with organization
const InviteMoverModalReview = ({
  form,
  field,
  setCurrentStepKind,
  viewer,
}: {
  form: any;
  field: string;
  setCurrentStepKind: (kind: string) => void;
  viewer: UserModel;
}) => {
  const responsive = useResponsive();

  return (
    <Container style={responsive.desktop ? {} : {backgroundColor: colors.white}}>
      <UserInfoPanel
        form={form}
        field={field}
        setCurrentStepKind={setCurrentStepKind}
        viewer={viewer}
      />
    </Container>
  );
};

export default InviteMoverModalReview;
